import { MessageInfo, SessionType } from '@/api/types/chat'
import { useFriendStore } from '@/stores/useFriendStore'
import { useGroupStore } from '@/stores/useGroupStore'
import { useChatStore } from '@/stores/useChatStore'
import { useTabAuth } from '@/hooks/useTabAuth'
import { useChatActions } from '@/hooks/useChatActions'
import NotificationManager from '@/utils/notification-manager'
import { logPrint } from '@/utils/my-utils'
import MessageManager from '@/utils/ably-im/message-manager'
import { useTranslation } from '@/hooks/useTranslation'

/**
 * 消息处理 Hook
 * 封装所有消息相关的处理逻辑，包括数据检查、会话验证、通知显示等
 */
export function useMessageHandler() {
  const tabAuth = useTabAuth()
  const { getConversations } = useChatActions()
  const notificationManager = NotificationManager.getInstance()
  const { autoTranslateMessage } = useTranslation()

  /**
   * 处理私聊消息的发送者信息
   * @param message 消息对象
   * @returns 组装后的消息或null
   */
  const handleDirectMessage = async (message: MessageInfo): Promise<MessageInfo | null> => {
    // 检查发送者是否在好友列表中
    let friendMap = useFriendStore.getState().friendMap
    if (!friendMap.has(message.sender_id ?? message.decryptedContentData?.senderId)) {
      logPrint('发送者不在好友列表中，重新加载好友列表')
      friendMap = await useFriendStore.getState().getFriendList()
    }

    // 重新检查并组装消息
    const friend = friendMap.get(message.sender_id ?? message.decryptedContentData?.senderId)
    if (friend) {
      const assembledMessage: MessageInfo = {
        ...message,
        senderName: friend.remark || friend.alias || friend.nickname,
        senderAvatar: friend.avatar,
        senderRemark: friend.remark
      }
      logPrint('私聊消息发送者信息已正确组装')
      return assembledMessage
    } else {
      logPrint('发送者不是我的好友，可能被删除了或屏蔽了')
      return null
    }
  }

  /**
   * 处理群聊消息的发送者信息
   * @param message 消息对象
   * @returns 组装后的消息或null
   */
  const handleGroupMessage = async (message: MessageInfo): Promise<MessageInfo | null> => {
    // 检查群组是否在群组列表中
    let groupMap = useGroupStore.getState().groupMap
    if (groupMap?.has(message.conversation_id ?? message.decryptedContentData?.conversationId) === false) {
      logPrint('群组不在群组列表中，重新加载群组列表')
      groupMap = await useGroupStore.getState().getGroupList()
    }

    // 确认我在这个群组中
    const group = groupMap.get(message.conversation_id ?? message.decryptedContentData?.conversationId)
    if (!group) {
      logPrint('我不在这个群组中，忽略消息')
      return null
    }

    // 先检查发送者是否是我的好友
    const friendMap = useFriendStore.getState().friendMap
    const friend = friendMap.get(message.sender_id ?? message.decryptedContentData?.senderId)
    if (friend) {
      // 如果是好友，使用好友信息
      const assembledMessage: MessageInfo = {
        ...message,
        senderName: friend.remark || friend.alias || friend.nickname,
        senderAvatar: friend.avatar,
        senderRemark: friend.remark
      }
      logPrint('群聊消息发送者是好友，使用好友信息')
      return assembledMessage
    }

    // 如果不是好友，通过智能缓存获取用户信息
    const userInfo = await useChatStore.getState().getUserByCacheOrNet(message.sender_id ?? message.decryptedContentData?.senderId)
    if (userInfo) {
      const assembledMessage: MessageInfo = {
        ...message,
        senderName: userInfo.nickname,
        senderAvatar: userInfo.avatar
      }
      logPrint('群聊消息发送者信息已获取')
      return assembledMessage
    }

    logPrint('无法获取群聊消息发送者信息')
    return null
  }

  /**
   * 确保必要的数据已加载（智能检查发送者信息）
   * @param message 消息对象
   */
  const ensureDataLoaded = async (message: MessageInfo): Promise<MessageInfo | null> => {
    // 检查是否是自己发送的消息
    if (message.sender_id === tabAuth.getUserId()) {
      return null // 自己的消息不需要检查发送者信息
    }

    try {
      // 根据会话类型分别处理
      if (message.session_type === 'direct') {
        return await handleDirectMessage(message)
      } else if (message.session_type === 'group') {
        return await handleGroupMessage(message)
      }

      return null
    } catch (error) {
      console.error('处理消息数据失败:', error)
      return null
    }
  }

  /**
   * 确保会话存在于会话列表中
   * @param conversationId 会话ID
   */
  const ensureConversationExists = async (conversationId: string): Promise<void> => {
    const conversations = useChatStore.getState().conversations
    const conversationExists = conversations.some((conv: any) => conv.id === conversationId)

    if (!conversationExists) {
      logPrint(`会话 ${conversationId} 不在会话列表中，重新获取会话列表`)

      try {
        // 重新获取会话列表
        await getConversations()

        // 验证会话是否已添加
        const updatedConversations = useChatStore.getState().conversations
        const nowExists = updatedConversations.some((conv: any) => conv.id === conversationId)
        if (nowExists) {
          logPrint(`会话 ${conversationId} 已成功添加到会话列表`)
        } else {
          console.warn(`会话 ${conversationId} 仍未出现在会话列表中，可能是新会话`)
        }
      } catch (error) {
        console.error('重新获取会话列表失败:', error)
      }
    } else {
      logPrint('会话列表正在加载中，跳过重复请求')
    }
  }

  /**
   * 显示消息通知
   * @param message 组装后的消息对象
   */
  const showMessageNotification = (message: MessageInfo): void => {
    notificationManager.showNotification(message)
  }

  /**
   * 处理收到的消息（主要处理方法）
   * @param message 原始消息对象
   */
  const handleIncomingMessage = async (message: MessageInfo): Promise<void> => {

    // 如果是拉黑的用户不显示通知
    if (useFriendStore.getState().blockedMap.has(message.sender_id)) {
      return
    }

    // 如果是群组消息，并且发消息的是自己，则不添加到会话
    if (message.session_type === 'group' && message.sender_id === tabAuth.getUserId()) {
      return
    }

    try {
      // 智能检查和加载数据, 返回组装好的数据, 如果数据不正确, 则返回null
      let finalMessage = await ensureDataLoaded(message)
      if (!finalMessage) {
        return
      }

      // 检查会话是否存在，如果不存在则刷新会话列表
      await ensureConversationExists(message.conversation_id)

      finalMessage = await translateMessage(finalMessage)
      logPrint('组装后的消息：', finalMessage)

      // 添加消息到会话
      useChatStore.getState().addMessage(message.conversation_id, finalMessage)

      // 如果不是当前活跃的会话，增加未读计数并显示通知
      const activeConversationId = useChatStore.getState().activeConversationId
      if (message.conversation_id !== activeConversationId) {
        useChatStore.getState().incrementUnreadCount(message.conversation_id)
        showMessageNotification(finalMessage)
      } else {
        MessageManager.getInstance().ackMessages([{
          conversation_id: message.conversation_id,
          message_id: message.message_id,
          session_type: message.session_type ?? SessionType.Direct
        }]);
      }
    } catch (error) {
      console.error('处理消息失败:', error)
    }
  }

  // 翻译消息
  const translateMessage = async (message: MessageInfo): Promise<MessageInfo> => {
    // 处理自动翻译收到的消息（如果启用）
    const chatStore = useChatStore.getState()
    const translationSettings = chatStore.getTranslationSettings(message.conversation_id)

    // 检查是否需要自动翻译收到的消息，且消息不是我发送的
    const currentUserId = tabAuth.getUserId() || ''
    const isIncomingMessage = message.sender_id !== currentUserId && message.sender_id

    if (isIncomingMessage && translationSettings.autoTranslateIncoming) {
      const messageContent = message.decryptedContentData?.content
      if (messageContent) {
        try {
          const result = await autoTranslateMessage(messageContent, translationSettings, true)
          if (result) {
            // 自动设置翻译内容，只更新翻译相关字段
            if (message.decryptedContentData) {
              message.decryptedContentData.showTranslate = true
              message.decryptedContentData.translateContent = result.translated_content
            }
            logPrint('自动翻译收到的消息成功:', result.translated_content)
          }
        } catch (error) {
          console.warn('自动翻译收到的消息失败:', error)
        }
      }
    }
    return message
  }

  /**
   * 处理通知点击事件
   * @param event 通知点击事件
   * @param navigateToUserChat 导航到用户聊天的回调
   * @param navigateToGroupChat 导航到群组聊天的回调
   */
  const handleNotificationClick = (
    event: CustomEvent,
    navigateToUserChat: (userId: string) => void,
    navigateToGroupChat: (conversationId: string) => void
  ): void => {
    const { message, conversationId, sessionType } = event.detail

    if (sessionType === 'group') {
      navigateToGroupChat(conversationId)
    } else {
      navigateToUserChat(message.sender_id)
    }
  }

  /**
   * 创建通知点击事件监听器
   * @param navigateToUserChat 导航到用户聊天的回调
   * @param navigateToGroupChat 导航到群组聊天的回调
   * @returns 事件处理函数
   */
  const createNotificationClickHandler = (
    navigateToUserChat: (userId: string) => void,
    navigateToGroupChat: (conversationId: string) => void
  ) => {
    return (event: any) => {
      handleNotificationClick(event, navigateToUserChat, navigateToGroupChat)
    }
  }

  /**
   * 清除未读计数
   */
  const clearUnreadCount = (): void => {
    notificationManager.clearUnreadCount()
  }

  /**
   * 设置未读计数
   * @param count 未读数量
   */
  const setUnreadCount = (count: number): void => {
    notificationManager.setUnreadCount(count)
  }

  return {
    // 主要处理方法
    handleIncomingMessage,

    // 通知相关方法
    showMessageNotification,
    createNotificationClickHandler,
    clearUnreadCount,
    setUnreadCount,

    // 工具方法（如果需要单独使用）
    ensureDataLoaded,
    ensureConversationExists
  }
}
